import { DateTime } from 'luxon';

/**
 * Horloge et erreurs de la salle de test Evergreen.
 *
 * La lecture suit la vidéo ; l'horloge ne sert qu'à savoir où reprendre (démarrage, rechargement,
 * retour d'onglet) et à recaler une avance manuelle.
 */

const ANCHOR_STORAGE_PREFIX = 'welya-evergreen-test-start-';

function anchorKey(testRunId: string): string {
  return `${ANCHOR_STORAGE_PREFIX}${testRunId}`;
}

/**
 * Heure locale de démarrage, posée à la réponse du POST : comparée à `Date.now()` du même poste,
 * elle neutralise une horloge en avance ou en retard.
 */
export function saveEvergreenTestAnchor(testRunId: string, startedAtLocalMs: number): void {
  try {
    window.sessionStorage.setItem(anchorKey(testRunId), String(startedAtLocalMs));
  } catch {
    // Stockage indisponible (navigation privée) : on retombera sur l'heure du back.
  }
}

export function readEvergreenTestAnchor(testRunId: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(anchorKey(testRunId));
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** Le back renvoie ses instants UTC sans fuseau : sans `zone`, ils seraient lus en heure locale. */
export function parseUtcMillis(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = DateTime.fromISO(value, { zone: 'utc' });
  return parsed.isValid ? parsed.toMillis() : null;
}

export function resolveEvergreenTestStartMs(params: {
  anchorMs: number | null;
  startedAt: string | null | undefined;
}): number | null {
  return params.anchorMs ?? parseUtcMillis(params.startedAt);
}

/** Position attendue d'après l'horloge, bornée à [0, durée]. */
export function evergreenTestClockSeconds(
  startMs: number,
  nowMs: number,
  durationSeconds: number,
): number {
  const elapsed = Math.max(0, (nowMs - startMs) / 1000);
  return Number.isFinite(durationSeconds) && durationSeconds >= 0
    ? Math.min(elapsed, durationSeconds)
    : elapsed;
}

export function isEvergreenTestRunExpired(
  expiresAt: string | null | undefined,
  nowMs: number,
): boolean {
  const expiresAtMs = parseUtcMillis(expiresAt);
  return expiresAtMs != null && nowMs >= expiresAtMs;
}

export type EvergreenTestErrorState =
  | 'FORBIDDEN'
  | 'NOT_PUBLISHED'
  | 'NOT_READY'
  | 'EXPIRED'
  | 'RETRY';

/** Un 404 sur l'espace de travail vise un non-membre : c'est un refus d'accès, pas une absence. */
export function resolveEvergreenTestError(error: unknown): EvergreenTestErrorState {
  const { status, message } =
    error != null && typeof error === 'object'
      ? (error as { status?: unknown; message?: unknown })
      : { status: undefined, message: undefined };
  const code = typeof message === 'string' ? message : '';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) {
    if (code.includes('WORKSPACE.NOT.FOUND')) return 'FORBIDDEN';
    if (code.includes('TEST_RUN.NOT_FOUND')) return 'EXPIRED';
    return 'NOT_PUBLISHED';
  }
  if (status === 409) {
    return code.includes('CONFIGURATION.NOT_READY') ? 'NOT_READY' : 'NOT_PUBLISHED';
  }
  return 'RETRY';
}
